import type { IncomeTestsOutput } from "./types.js";

// Plain-English copy for the shared income-tests engine. Lives in the rules
// package so every surface that explains why "the same benefit counts as a
// different number for each test" renders identical wording from one source.
// Information, not advice (validation §3): we state what each derived figure IS
// and how it's built from ATO / Services Australia / DSS definitions — no
// "save $X", no "switch", no product recommendation. Pure string-builders.

/** Whole-dollar AUD with thousands separators, rendered the same everywhere. */
export function formatAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

/** Names the four derived figures for a result surface. */
export function incomeFigureLabels(): Record<string, string> {
  return {
    incomeForSurchargePurposes: "Income for surcharge purposes",
    helpRepaymentIncome: "HELP/STSL repayment income",
    div293Income: "Division 293 income",
    adjustedTaxableIncome: "Adjusted taxable income",
  };
}

/**
 * Explains the RFBA gross-up asymmetry in information-only terms — the load-
 * bearing mechanic. States the mechanism without overstating any CCS/FTB loss.
 */
export function fringeBenefitAsymmetryBullet(out: IncomeTestsOutput): string {
  if (out.grossedUpFringeBenefits === 0) {
    return "You have no reportable fringe benefits, so the same figure flows into each income test.";
  }
  return (
    `Your reportable fringe benefits count at their full reported value ` +
    `(${formatAud(out.grossedUpFringeBenefits)}) for the Medicare levy surcharge, ` +
    `Division 293 and HELP/STSL repayment income. For the family-assistance ` +
    `adjusted taxable income (Child Care Subsidy and Family Tax Benefit), the ` +
    `exempt-employer portion is counted at a reduced value, giving an adjusted ` +
    `fringe benefits total of ${formatAud(out.adjustedFringeBenefitsTotal)}. ` +
    `That is why the same benefit can appear as a different number in different tests.`
  );
}

/** Source-attribution line, composed from the ruleset metadata in force. */
export function sourceBullet(ruleVersion: string, verifiedAt: string): string {
  return (
    `These figures use the ATO, Services Australia and DSS income-test ` +
    `definitions in force for ${ruleVersion} (last verified ${verifiedAt}). ` +
    `Educational tool. Not financial advice. The ATO and Services Australia ` +
    `finalise your actual figures — always check ato.gov.au and ` +
    `servicesaustralia.gov.au.`
  );
}

export function explanationBullets(
  out: IncomeTestsOutput,
  ruleVersion: string,
  verifiedAt: string,
): string[] {
  return [
    fringeBenefitAsymmetryBullet(out),
    sourceBullet(ruleVersion, verifiedAt),
  ];
}
